"use client";

import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useModalOutsideClick from "@repo/shared-ui/useModalOutsideClick";

interface WatchListPortalProps {
  isOpen: boolean;
  title: string;
  description?: string;
  buttonText?: string;
  subtitle?: string;
  children: ReactNode;
  onClose: () => void;
  enableScroll?: boolean;
  onPrimaryButtonClick?: () => void;
  showHeader?: boolean;
  contentClassName?: string;
  errorMessage?: string;
  primaryButtonClassNames?: string;
  onBackdropClick?: (e: React.MouseEvent) => void;
}

const WatchListPortal: FC<WatchListPortalProps> = ({
  isOpen,
  title,
  subtitle,
  description,
  buttonText = "",
  children,
  onClose,
  enableScroll = true,
  showHeader,
  onPrimaryButtonClick,
  contentClassName,
  primaryButtonClassNames,
  errorMessage = "",
  onBackdropClick,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);

  const { handleBackdropClick, handleModalDialogClick } = useModalOutsideClick({
    onClose,
    onBackdropClick,
  });

  useEffect(() => {
    const el = contentRef.current;

    const handleScroll = () => {
      if (!el) return;

      // Show scrollbar while scrolling
      setIsScrolling(true);

      // Hide scrollbar after 1 second of no scrolling
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
      scrollTimeout.current = setTimeout(() => {
        setIsScrolling(false);
      }, 1000);
    };

    if (el && enableScroll) {
      el.addEventListener("scroll", handleScroll);
      handleScroll();
    }

    return () => {
      if (el && enableScroll) {
        el.removeEventListener("scroll", handleScroll);
      }
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, [isOpen, enableScroll]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Build content className
  const baseClasses = "px-4 text-xs md:text-sm";
  let contentClasses = baseClasses;
  if (enableScroll) {
    const scrollClasses = "watchlist-portal-scroll overflow-auto pb-18 md:pb-0";
    const heightClass = contentClassName ?? "max-h-[306px]";
    const scrollingClass = isScrolling ? "scrolling" : "";
    contentClasses = `${baseClasses} ${scrollClasses} ${heightClass} ${scrollingClass}`;
  }

  return createPortal(
    <>
      <button
        className="fixed inset-0 z-[100000] bg-black/50"
        onClick={handleBackdropClick}
        aria-label="Close modal backdrop"
      />
      <div
        className="fixed inset-0 z-[100001] flex items-end justify-center md:items-center lg:px-4"
        onClick={handleModalDialogClick}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            onClose();
          }
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "dialog-title" : undefined}
        tabIndex={-1}
      >
        <div
          className="relative w-full rounded-[4px] bg-white px-4 pb-4 pt-4 md:h-[441px] md:w-[460px] lg:max-w-[460px]"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          aria-label={title ? `Modal content: ${title}` : "Modal content"}
        >
          {showHeader !== false && (
            <div className="text-center">
              <div className="flex justify-end">
                <button
                  onClick={onClose}
                  className="inline-block cursor-pointer border-0 bg-transparent p-0 text-xs text-link-primary"
                  aria-label="close modal"
                >
                  <i className="icomoon before:content-['\e914']"></i>
                </button>
              </div>
              {title && (
                <h2 id="dialog-title" className="text-lg font-medium leading-[21.33px] text-gray-900">
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="mt-1 text-xs text-gray-900">{subtitle}</p>
              )}
              {description && <p className="mt-2 text-xs">{description}</p>}
            </div>
          )}

          <div
            ref={contentRef}
            className={contentClasses}
          >
            {children}
          </div>

          <div className="absolute bottom-0 left-0 right-0 z-[99999] flex w-full flex-col items-center justify-center rounded-b-[4px] bg-white px-4 py-4 shadow-[0px_0px_10px_0px_rgba(0,0,0,0.2)]">
            {errorMessage && (
              <p className="mb-3 text-center text-xs font-normal text-red-600">
                {errorMessage}
              </p>
            )}

            {buttonText && (
              <button
                onClick={onPrimaryButtonClick}
                className={`${primaryButtonClassNames ?? ""} flex h-10 w-full min-w-[200px] cursor-pointer items-center justify-center rounded-[200px] border-0 bg-gradient-to-r from-[#ff6700] to-[#ff8900] px-6 py-3 text-white transition-opacity hover:opacity-90 md:w-auto md:min-w-[200px]`}
                aria-label={buttonText}
              >
                {buttonText}
              </button>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default WatchListPortal;
